import { createContext, useCallback, useContext, useRef } from 'react';

import { captureSentry } from '@/utils/helpers/sentryHelper';

type Subscriber<T = any> = {
  callbacks: Array<{ id: string; onData: (data: T) => void; onError?: (error: any) => void }>;
  unsubscribe: () => void;
  count: number;
};

//NOTE: should be `query + JSON.stringify(vars)` so that subscriptions with filters still have a different socket
type SubscriptionKey = string;

type SubscriptionManagerContextType = {
  addSubscriber: <T>(
    key: SubscriptionKey,
    customName: string,
    query: string,
    variables: object,
    callback: (data: T) => void,
    onError?: (error: string) => void
  ) => Promise<string>;
  removeSubscriber: (key: SubscriptionKey, callbackId: string) => void;
  generateSubscriptionKey: (query: string, variables: object) => SubscriptionKey;
};

const SubscriptionManagerContext = createContext<SubscriptionManagerContextType | null>(null);

export const useSubscriptionManager = () => {
  const ctx = useContext(SubscriptionManagerContext);
  if (!ctx)
    throw new Error('useSubscriptionManager must be used within a SubscriptionManagerProvider');
  return ctx;
};

export const SubscriptionManagerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  /*
  NOTE: we have theis mutex system here because we have race conditions for example:
  If multiple places call useMeetingParticipantList, which all call a subscription onUpdateUserMeetingJoin,
  they will all try to create at the same time and pass the check if the key is not yet present in the
  activeSubscriptions map, and then they will all create the subscription.
  This is a problem because the subscription will be created multiple times, using this mutex system we
  ensure that only one subscription is created at a time for one key.
  */
  const mutexes = useRef<Set<SubscriptionKey>>(new Set());
  const activeSubscriptions = useRef<Map<SubscriptionKey, Subscriber>>(new Map());

  const awaitMutex = useCallback((key: SubscriptionKey) => {
    return new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        clearInterval(interval);
        captureSentry({
          title: 'Mutex timeout in SubscriptionManager',
          detail: {
            key,
            activeSubscriptions: Array.from(activeSubscriptions.current.keys()),
            mutexes: Array.from(mutexes.current),
          },
        });
        reject(new Error('Mutex timeout in SubscriptionManager'));
      }, 5000); // if after 5 seconds the mutex is not released, we flag the error

      const interval = setInterval(() => {
        if (!mutexes.current.has(key)) {
          clearTimeout(timeout);
          clearInterval(interval);
          resolve();
        }
      }, 100);
    });
  }, []);

  const addSubscriber = useCallback(
    async <T,>(
      key: SubscriptionKey,
      customName: string,
      query: string,
      variables: object,
      callback: (data: T) => void,
      onError?: (error: string) => void
    ) => {
      const id = crypto.randomUUID();

      await awaitMutex(key);
      mutexes.current.add(key);

      console.log('DEBUG >> submanager addSubscriber', key, activeSubscriptions.current.has(key));

      if (activeSubscriptions.current.has(key)) {
        const sub = activeSubscriptions.current.get(key)!;
        sub.callbacks.push({ id, onData: callback, onError });
        sub.count++;
        mutexes.current.delete(key);
        return id;
      }

      console.log('DEBUG >> submanager adding actual subscription', key);
      // NIKO TODO: check if we will need the subscription manager (if we call one sub in two places, does it create 1 or 2 subscriptions?)
      // const observer = await graphqlSubscriber(query, variables);
      // const subscription = observer.subscribe({
      //   next: (res: any) => {
      //     console.log('DEBUG >> submanager got data for ', key);
      //     activeSubscriptions.current.get(key)?.callbacks.forEach((cb) => cb.onData(res.data));
      //   },
      //   error: (err) => {
      //     activeSubscriptions.current.get(key)?.callbacks.forEach((cb) => {
      //       if (cb.onError) {
      //         cb.onError(err);
      //       }
      //     });
      //     console.error('Subscription error:', err);
      //     captureSentry({
      //       title: `Subscription error: ${key}`,
      //       error: err,
      //     });
      //     setTimeout(() => reloadApp(), 3000);
      //   },
      // });

      // activeSubscriptions.current.set(key, {
      //   callbacks: [{ id, onData: callback, onError }],
      //   unsubscribe: () => subscription.unsubscribe(),
      //   count: 1,
      // });

      mutexes.current.delete(key);
      return id;
    },
    []
  );

  const removeSubscriber = useCallback((key: SubscriptionKey, callbackId: string) => {
    const sub = activeSubscriptions.current.get(key);
    console.log('DEBUG >> submanager removeSubscriber', key, callbackId, sub);
    if (!sub) return;

    sub.callbacks = sub.callbacks.filter((cb) => cb.id !== callbackId);
    sub.count--;

    if (sub.count === 0) {
      sub.unsubscribe();
      activeSubscriptions.current.delete(key);
    }
  }, []);

  const generateSubscriptionKey = useCallback((query: string, variables: object) => {
    const subName = (() => {
      const matches = query.match(/\{\s*(on[A-Z][a-zA-Z0-9_]*)\s*\(/);
      if (matches && matches.length > 1) {
        return matches[1];
      }
      return null;
    })();

    if (!subName) {
      captureSentry({
        title: 'No subname in generateSubscriptionKey',
        detail: {
          query: query,
        },
      });
      return `${query}-${JSON.stringify(variables)}`;
    }
    return `${subName}-${JSON.stringify(variables)}`;
  }, []);

  // useEffect(() => {
  //   setInterval(() => {
  //     console.log('DEBUG >> activeSubscriptions', activeSubscriptions.current);
  //   }, 1000)
  // }, [])

  return (
    <SubscriptionManagerContext.Provider
      value={{ addSubscriber, removeSubscriber, generateSubscriptionKey }}
    >
      {children}
    </SubscriptionManagerContext.Provider>
  );
};
